import { useEffect, useCallback } from 'react';

import { useFeature } from '@growthbook/growthbook-react';
import {
  useAuthenticationStore,
  authenticationStoreSelector,
} from '@repo/editor/src/stores/authenticationStore';
import {
  useUserStore,
  userStoreSelector,
} from '@repo/editor/src/stores/userStore';

import useWindowSize from 'global/utils/useWindowSize';

/**
 * Force user to authenticate on a page
 */
const useForcedAuthentication = ({
  enabled,
  openWithoutDelay,
}: {
  enabled: boolean;
  openWithoutDelay?: boolean;
}): void => {
  const { isMobile } = useWindowSize();
  const forceSignupTestEnabled = useFeature('force-signup').value;
  const user = useUserStore(userStoreSelector.loadingUser);
  const openAuthentication = useAuthenticationStore(
    authenticationStoreSelector.openAuthentication
  );

  const openModal = useCallback(() => {
    if (enabled && user === null) {
      openAuthentication(
        {
          enableClose: !forceSignupTestEnabled,
          blurBackground: forceSignupTestEnabled,
          enableBackgroundScrolling: !forceSignupTestEnabled,
        },
        true
      );
    }
  }, [enabled, user, openAuthentication, forceSignupTestEnabled]);

  useEffect(() => {
    if (openWithoutDelay || forceSignupTestEnabled) {
      openModal();
    }
  }, [openWithoutDelay, user, openModal, forceSignupTestEnabled]);

  const openModalOnScroll = useCallback(() => {
    // A guest can view ~15-20 templates (on mobile) or 3 * full screen height content (desktop) before auth modal shows
    const credit = isMobile ? window.innerHeight * 15 : window.innerHeight * 3;
    if (window.scrollY >= credit) {
      openModal();
      window.removeEventListener('scroll', openModalOnScroll);
    }
  }, [isMobile, openModal]);

  useEffect(() => {
    if (enabled) {
      window.addEventListener('scroll', openModalOnScroll);
    }

    return () => window.removeEventListener('scroll', openModalOnScroll);
  }, [enabled, openModalOnScroll]);
};

export default useForcedAuthentication;
